/**
 * The three ways to give this app a position.
 *
 *   A. **Chart** — long-press the map. Handled by MapPage; this offers the link.
 *   B. **Type it** — two fields, tolerant of every format a bridge uses.
 *   C. **Camera** — photograph the chartplotter and read the coordinates off it.
 *
 * Plus "use my position", which is the fourth and is the right answer when the
 * vessel is already there.
 *
 * All three converge on the same rule: **the position is shown on the chart
 * before it is accepted.** For A that is inherent. For B and C it is the point
 * of the preview — a transposed digit is obvious as a pin in the wrong fjord
 * and invisible as text. C in particular never commits a reading on its own;
 * OCR proposes candidates and the captain confirms one.
 *
 * The camera image never leaves the device. See lib/ocr.ts.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  COORD_FORMATS,
  coordFormatInfo,
  Notice,
  formatPosition,
  parseAxis,
  toRunTogether,
  withDefaultHemisphere,
  type CoordFormat,
  type PositionCandidate,
} from '@abg/ui'
import { GeolocationFailed, getFix, looksNorwegian } from '../lib/geo'
import {
  bitmapFromFile,
  ocrAvailability,
  readPosition,
  type OcrResult,
  terminateOcr,
  type OcrAvailability,
} from '../lib/ocr'
import { PositionPreview } from './PositionPreview'

type Mode = 'idle' | 'type' | 'camera'

/**
 * The guide box, as fractions of the video frame.
 *
 * Used twice: to position the overlay the captain aims with, and to crop the
 * region handed to the recogniser. It has to be one constant, because the two
 * drifting apart means the captain frames one thing and the app reads another
 * -- worse than no guide box, because it looks like it works.
 *
 * A wide, short band: two lines of coordinates and nothing else. Excluding SOG,
 * COG, depth and the datum label is most of what makes the read reliable.
 */
const GUIDE = { x: 0.05, y: 0.34, width: 0.9, height: 0.32 } as const

export interface PositionEntryProps {
  lat: number | null
  lon: number | null
  onChange: (lat: number, lon: number, source: 'gps' | 'typed' | 'ocr') => void
  /** Opens the chart picker. Provided by the page, which owns navigation. */
  onPickOnChart: () => void
}

export function PositionEntry({ lat, lon, onChange, onPickOnChart }: PositionEntryProps) {
  const [mode, setMode] = useState<Mode>('idle')
  /**
   * A reading handed over from the camera for correction.
   *
   * Kept here rather than inside TypedEntry because the camera is a sibling:
   * editing is a handover between two modes, not state either owns.
   */
  const [seed, setSeed] = useState<{ lat: number; lon: number } | null>(null)
  const [ocr, setOcr] = useState<OcrAvailability>('unsupported')
  const [locating, setLocating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  useEffect(() => {
    void ocrAvailability().then(setOcr)
    return () => void terminateOcr()
  }, [])

  const useMyPosition = useCallback(async () => {
    setLocating(true)
    setError(null)
    setNote(null)
    try {
      const fix = await getFix()
      onChange(fix.lat, fix.lon, 'gps')
      setNote(`From this device, ±${Math.round(fix.accuracyM)} m.`)
      setMode('idle')
    } catch (caught) {
      setError(caught instanceof GeolocationFailed ? caught.message : 'Could not get a fix.')
    } finally {
      setLocating(false)
    }
  }, [onChange])

  const hasPosition = lat !== null && lon !== null

  return (
    <section className="card stack">
      <div className="row-between">
        <strong>Position</strong>
        {hasPosition && !looksNorwegian(lat, lon) && (
          <span className="badge badge-warn">outside our area</span>
        )}
      </div>

      {hasPosition ? (
        <>
          <div className="mono" style={{ fontSize: 'var(--fs-body-lg)' }}>
            {formatPosition(lat, lon)}
          </div>
          <PositionPreview lat={lat} lon={lon} />
        </>
      ) : (
        <p className="muted small">
          No position yet. Pick one of the four ways below — whichever is quickest where you are
          standing.
        </p>
      )}

      {error && <Notice tone="alert">{error}</Notice>}
      {note && !error && <p className="caption">{note}</p>}

      <div className="entry-modes">
        <button type="button" className="btn btn-quiet" onClick={useMyPosition} disabled={locating}>
          {locating ? 'Locating…' : '⌖ My position'}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onPickOnChart}>
          ⊕ On the chart
        </button>
        <button
          type="button"
          className={mode === 'type' ? 'btn' : 'btn btn-quiet'}
          onClick={() => {
            // Clear any camera reading: pressing "Type it" is a fresh start,
            // and a prefilled field would be a position the captain did not
            // put there.
            setSeed(null)
            setMode(mode === 'type' ? 'idle' : 'type')
          }}
        >
          ⌨ Type it
        </button>
        {ocr !== 'unsupported' && (
          <button
            type="button"
            className={mode === 'camera' ? 'btn' : 'btn btn-quiet'}
            onClick={() => setMode(mode === 'camera' ? 'idle' : 'camera')}
          >
            ⛶ Read the plotter
          </button>
        )}
      </div>

      {mode === 'type' && (
        <TypedEntry
          // Remount when a new reading arrives, so the fields take it as their
          // initial value. Without the key, React keeps the old state and the
          // panel would silently ignore the handover.
          key={seed ? `${seed.lat},${seed.lon}` : 'blank'}
          seed={seed}
          onAccept={(nextLat, nextLon) => {
            onChange(nextLat, nextLon, 'typed')
            setMode('idle')
            setNote('Entered by hand. Check the pin on the chart above.')
          }}
        />
      )}

      {mode === 'camera' && (
        <CameraEntry
          onEdit={(candidate) => {
            setSeed({ lat: candidate.lat, lon: candidate.lon })
            setMode('type')
          }}
          availability={ocr}
          onAccept={(candidate) => {
            onChange(candidate.lat, candidate.lon, 'ocr')
            setMode('idle')
            setNote(
              `Read from the plotter photo (${candidate.matched}). Check the pin on the chart above.`,
            )
          }}
        />
      )}
    </section>
  )
}

// --------------------------------------------------------------------- typed

/**
 * Two fields, not one.
 *
 * A single "paste a coordinate here" box has to guess where latitude ends and
 * longitude begins, and guesses wrong on exactly the inputs that matter. Two
 * fields make the split the captain's, and let the parser refuse an `E` typed
 * into the latitude field rather than silently reinterpreting it — swapped
 * lat/lon is the classic way to end up three thousand miles out.
 */
function TypedEntry({
  onAccept,
  seed,
}: {
  onAccept: (lat: number, lon: number) => void
  /** A camera reading to correct rather than retype. See toRunTogether. */
  seed?: { lat: number; lon: number } | null
}) {
  const [latText, setLatText] = useState(() =>
    seed ? toRunTogether(seed.lat, 'lat') : '',
  )
  const [lonText, setLonText] = useState(() =>
    seed ? toRunTogether(seed.lon, 'lon') : '',
  )
  /**
   * Degrees and decimal minutes by default, because that is what this app
   * displays (decision 15) and what a bridge readout shows.
   *
   * The setting is **advisory, not a mode**: it decides how digits run
   * together are read, so `6723.850` works, and it chooses the placeholder.
   * It does not refuse a position typed some other way -- a captain pasting
   * `67.85367` out of an email while this says degrees and minutes gets their
   * position, and the line under the field says how it was read. Refusing
   * input we understand would be a worse app, and the pin on the chart below
   * is what actually catches a misread (decision 27).
   */
  const [format, setFormat] = useState<CoordFormat>('dm')
  const chosen = coordFormatInfo(format)

  /**
   * The hemisphere is assumed, not asked for. See withDefaultHemisphere: north
   * and east is right for every position this system holds, so there is no
   * control to tap and the field can be a number pad.
   *
   * A letter still works if it arrives -- pasted, or typed on a real keyboard.
   * Nothing is stripped, so `60 23.850 S` parses as south, and an `E` pasted
   * into the latitude field still earns the parser's "did latitude and
   * longitude get swapped?". Whatever was assumed shows up in the read-back
   * and as a pin on the chart before it can be used.
   */
  const latResult = latText.trim()
    ? parseAxis(withDefaultHemisphere(latText, 'lat'), 'lat', format)
    : null
  const lonResult = lonText.trim()
    ? parseAxis(withDefaultHemisphere(lonText, 'lon'), 'lon', format)
    : null
  const ready = latResult?.ok && lonResult?.ok

  return (
    <div className="stack-sm entry-panel">
      <div className="field">
        <label htmlFor="coord-format">Coordinate format</label>
        <select
          id="coord-format"
          value={format}
          onChange={(e) => setFormat(e.target.value as CoordFormat)}
        >
          {COORD_FORMATS.map((f) => (
            <option key={f.key} value={f.key}>
              {f.labelEn}
            </option>
          ))}
        </select>
      </div>

      <p className="caption">
        WGS 84 — <span className="mono">{chosen.exampleLat}</span>, the way a plotter
        prints it. North and east are assumed; type or paste an S or a W if you need one.
        The line under each field says how it was read.
      </p>

      {/* `decimal` rather than `numeric`: iOS draws `numeric` with no decimal
        * separator on the pad at all, which would leave 23.850 minutes
        * untypeable on the device this screen is mostly used on. */}
      <div className="field">
        <label htmlFor="lat-input">Latitude</label>
        <input
          id="lat-input"
          type="text"
          className="mono"
          value={latText}
          onChange={(e) => setLatText(e.target.value)}
          placeholder={chosen.exampleLat}
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
        />
        {latResult && !latResult.ok && <span className="error">{latResult.error}</span>}
        {latResult?.ok && (
          <span className="hint">Read as {latResult.interpretation}.</span>
        )}
      </div>

      <div className="field">
        <label htmlFor="lon-input">Longitude</label>
        <input
          id="lon-input"
          type="text"
          className="mono"
          value={lonText}
          onChange={(e) => setLonText(e.target.value)}
          placeholder={chosen.exampleLon}
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        {lonResult && !lonResult.ok && <span className="error">{lonResult.error}</span>}
        {lonResult?.ok && <span className="hint">Read as {lonResult.interpretation}.</span>}
      </div>

      {ready && latResult.ok && lonResult.ok && (
        <>
          <div className="notice">
            <div className="mono">{formatPosition(latResult.value, lonResult.value)}</div>
          </div>
          <PositionPreview lat={latResult.value} lon={lonResult.value} height={140} />
          <button
            type="button"
            className="btn btn-block"
            onClick={() => onAccept(latResult.value, lonResult.value)}
          >
            Use this position
          </button>
        </>
      )}
    </div>
  )
}

// -------------------------------------------------------------------- camera

/**
 * Photograph the plotter, read the coordinates off it, confirm on the chart.
 *
 * The guide box is not decoration. Only the region inside it is sent to the
 * recogniser, which is what keeps SOG, COG, depth, distance-to-waypoint and the
 * datum label out of the text — and those are precisely the numbers that
 * produce a plausible-looking wrong answer. Frame the two coordinate lines and
 * nothing else.
 */
function CameraEntry({
  availability,
  onAccept,
  onEdit,
}: {
  availability: OcrAvailability
  onAccept: (candidate: PositionCandidate) => void
  /** Hand this reading to the typed panel for correction. */
  onEdit: (candidate: PositionCandidate) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [streaming, setStreaming] = useState(false)
  // Where the guide box sits, in CSS pixels. Measured rather than expressed as
  // percentages of the container: the frame is letterboxed inside it whenever
  // the stream's aspect ratio differs from the box's, and percentages of the
  // box would then point somewhere else in the image.
  const [guideBox, setGuideBox] = useState<{
    left: number
    top: number
    width: number
    height: number
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [candidates, setCandidates] = useState<PositionCandidate[] | null>(null)
  const [rawText, setRawText] = useState('')
  const [polarity, setPolarity] = useState<OcrResult['polarity'] | null>(null)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    setStreaming(false)
  }, [])

  useEffect(() => stop, [stop])

  // Recompute whenever the box resizes or the stream's dimensions arrive.
  // `object-fit: contain` centres the frame and scales it to fit, so the
  // fractions apply to that content rect -- not to the element.
  useEffect(() => {
    const video = videoRef.current
    const frame = frameRef.current
    if (!video || !frame) return

    const measure = () => {
      const { videoWidth, videoHeight } = video
      const box = frame.getBoundingClientRect()
      if (!videoWidth || !videoHeight || !box.width) {
        setGuideBox(null)
        return
      }
      const scale = Math.min(box.width / videoWidth, box.height / videoHeight)
      const contentWidth = videoWidth * scale
      const contentHeight = videoHeight * scale
      const offsetX = (box.width - contentWidth) / 2
      const offsetY = (box.height - contentHeight) / 2
      setGuideBox({
        left: offsetX + GUIDE.x * contentWidth,
        top: offsetY + GUIDE.y * contentHeight,
        width: GUIDE.width * contentWidth,
        height: GUIDE.height * contentHeight,
      })
    }

    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    video.addEventListener('loadedmetadata', measure)
    measure()
    return () => {
      observer.disconnect()
      video.removeEventListener('loadedmetadata', measure)
    }
  }, [streaming])

  const start = useCallback(async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The rear camera, and as much resolution as the device will give: the
        // text is small in frame and every pixel helps the recogniser.
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      setStreaming(true)
    } catch {
      setError(
        'Could not open the camera. Allow camera access, or take a photo and pick the file below.',
      )
    }
  }, [])

  const recognise = useCallback(
    async (
      source: CanvasImageSource,
      width: number,
      height: number,
      useGuide = true,
    ) => {
      setBusy(true)
      setProgress(0)
      setError(null)
      setCandidates(null)
      try {
        const region = useGuide
          ? {
              x: Math.round(width * GUIDE.x),
              y: Math.round(height * GUIDE.y),
              width: Math.round(width * GUIDE.width),
              height: Math.round(height * GUIDE.height),
            }
          : { x: 0, y: 0, width, height }
        const result = await readPosition(source, region, setProgress)
        setRawText(result.text)
        setPolarity(result.polarity)
        setCopied(false)
        setCandidates(result.candidates)
        if (result.candidates.length === 0) {
          setError(
            'No coordinates found in that frame. Fill the guide box with just the two ' +
              'coordinate lines, hold steady, and avoid glare on the screen.',
          )
        }
      } catch {
        setError('Could not read the image. Type the position instead.')
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const capture = useCallback(() => {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    void recognise(video, video.videoWidth, video.videoHeight)
  }, [recognise])

  if (availability === 'no-assets') {
    return (
      <div className="entry-panel">
        <Notice tone="warn">
          Reading the plotter is not set up on this installation. Type the position instead — it
          takes about as long.
        </Notice>
      </div>
    )
  }

  return (
    <div className="stack-sm entry-panel">
      <p className="caption">
        Point the camera at the plotter's position readout and fill the box with just the two
        coordinate lines. The photo stays on this device — it is never uploaded or saved.
      </p>

      <div className="camera-frame" ref={frameRef}>
        <video ref={videoRef} playsInline muted className="camera-video" />
        {streaming && guideBox && (
          <div
            className="camera-guide"
            aria-hidden="true"
            style={{
              left: guideBox.left,
              top: guideBox.top,
              width: guideBox.width,
              height: guideBox.height,
            }}
          />
        )}
        {!streaming && (
          <button type="button" className="btn camera-start" onClick={start}>
            Open camera
          </button>
        )}
      </div>

      {streaming && (
        <button type="button" className="btn btn-lg btn-block" onClick={capture} disabled={busy}>
          {busy ? `Reading… ${Math.round(progress * 100)}%` : 'Read the position'}
        </button>
      )}

      <label className="btn btn-quiet btn-block" style={{ cursor: 'pointer' }}>
        Or pick a photo
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="visually-hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            if (!file) return
            try {
              const bitmap = await bitmapFromFile(file)
              // A still the captain framed themselves is read whole: cropping
              // to a guide box they never saw would silently discard the part
              // they were aiming at.
              await recognise(bitmap, bitmap.width, bitmap.height, false)
            } catch {
              setError('Could not open that image.')
            }
          }}
        />
      </label>

      {error && <Notice tone="alert">{error}</Notice>}

      {candidates && candidates.length > 0 && (
        <div className="stack-sm">
          <strong>{candidates.length === 1 ? 'Is this it?' : 'Which one?'}</strong>
          {candidates.length > 1 && (
            <p className="caption">
              The plotter showed more than one position — often the cursor and the vessel. Only you
              know which you meant.
            </p>
          )}
          {candidates.map((candidate, index) => (
            <div key={`${candidate.lat}-${candidate.lon}-${index}`} className="card card-tight stack-sm">
              <div className="row-between">
                <span className="mono">{formatPosition(candidate.lat, candidate.lon)}</span>
                <span
                  className={candidate.confidence >= 0.7 ? 'badge badge-ok' : 'badge badge-warn'}
                >
                  {candidate.confidence >= 0.7 ? 'clear read' : 'unsure'}
                </span>
              </div>
              <div className="caption mono">read as “{candidate.matched}”</div>
              {!looksNorwegian(candidate.lat, candidate.lon) && (
                <Notice tone="warn">
                  That is outside the area we cover — likely a misread digit.
                </Notice>
              )}
              <PositionPreview lat={candidate.lat} lon={candidate.lon} height={140} />
              <button
                type="button"
                className="btn btn-block"
                onClick={() => {
                  stop()
                  onAccept(candidate)
                }}
              >
                Use this position
              </button>
              {/* The third choice, and often the one that is wanted: a read
                * that got every digit right and one separator wrong is not
                * something to accept or to redo. It hands the reading to the
                * typed panel, which already has the number pad, the format
                * selector and the chart -- and decision 27 still holds,
                * because the corrected value lands on that chart before it
                * can be used. */}
              <button
                type="button"
                className="btn btn-quiet btn-block"
                onClick={() => {
                  stop()
                  onEdit(candidate)
                }}
              >
                Nearly right — let me fix it
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Shown for **every** read, not only a failed one.
        *
        * A read that finds nothing is obvious. A read that finds a plausible
        * *wrong* position is the dangerous one, and it used to have no
        * diagnostic at all -- no way to see whether a digit was misread or the
        * two lines were paired wrongly. Open by default when nothing was
        * found, collapsed when there is something to choose from, because then
        * the candidates are the point and this is reference.
        *
        * The copy button exists because the alternative on a phone is fighting
        * iOS selection inside a scrolling `<pre>`, and this text is worth more
        * to whoever is tuning `preprocess()` than to the captain. */}
      {candidates !== null && rawText.trim() && (
        <details open={candidates.length === 0}>
          <summary className="caption">What the camera actually read</summary>
          {polarity && (
            <p className="caption" style={{ margin: 'var(--space-2) 0 0' }}>
              {polarity === 'as-photographed' && 'Read from the photo as taken.'}
              {polarity === 'inverted' && 'Read after inverting the photo.'}
              {polarity === 'neither' && 'Neither the photo nor its inverse gave a position.'}
            </p>
          )}
          <pre className="code-read">{rawText.trim()}</pre>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => {
              // Called straight from the gesture with no await in front of it:
              // Safari refuses a clipboard write that is not synchronous with
              // the tap. Same rule as admin-web/src/lib/clipboard.ts.
              navigator.clipboard
                ?.writeText(rawText.trim())
                .then(() => setCopied(true))
                .catch(() => setCopied(false))
            }}
          >
            {copied ? 'Copied' : 'Copy this text'}
          </button>
        </details>
      )}
    </div>
  )
}
